// Loreto content ground truth (mock data) — single source for the page

export const VIDEO_SOURCES = [
  { src: '/media/neo-downloading-skills.webm', type: 'video/webm' },
  { src: '/media/neo-downloading-skills.mp4',  type: 'video/mp4' },
];

export const NAV_LINKS = [
  { label: 'Studio', href: '#studio' },
  { label: 'Sources', href: '#sources' },
  { label: 'Skills', href: '#skills' },
  { label: 'How', href: '#how' },
  { label: 'Stats', href: '#stats' },
];

export const HERO_STATES = [
  { id: 1, label: 'Dormant',     text: 'Your agent only knows what it was trained on.' },
  { id: 2, label: 'Influx',      text: 'Point Loreto at a video, a PDF, an article.' },
  { id: 3, label: 'Materialize', text: 'It extracts the knowledge as a structured skill.' },
  { id: 4, label: 'Ready',       text: 'Claude picks it up automatically. It just knows now.' },
];

export const SOURCES = [
  'YouTube',
  'Articles',
  'PDFs',
  'Images',
  'Long-form video',
  'Research papers',
  'Technical docs',
  'Conference talks',
];

export const PILLARS = [
  {
    n: '01',
    title: 'Any source.',
    body: 'YouTube, article, PDF, image — one endpoint. Loreto auto-detects the type and ingests it. You stop wiring connectors and start collecting capability.',
    tag: 'Universal ingest',
  },
  {
    n: '02',
    title: 'Full-spectrum extraction.',
    body: 'Claude does not just summarize the topic. Loreto identifies every generalizable principle the source contains — including the ones you would never have thought to ask for.',
    tag: 'Deep parse',
  },
  {
    n: '03',
    title: 'Production-ready packages.',
    body: 'Each run returns ranked skills with SKILL.md, README.md, reference stubs, and runnable test scripts. No shaping, no cleanup. Drop them in and ship.',
    tag: 'Ranked output',
  },
];

export const SHOWCASE = [
  {
    slug: 'auditing-intelligence-context-fit',
    title: 'auditing-intelligence-context-fit',
    summary: 'Audits whether the intelligence available to an agent actually fits the context it operates in. Surfaces the gaps before they become incidents.',
    meta: 'Skill package · context audit',
    href: '/skills/auditing-intelligence-context-fit',
  },
  {
    slug: 'benchmarking-ai-agents-beyond-models',
    title: 'benchmarking-ai-agents-beyond-models',
    summary: 'Benchmarks agents on what the model alone cannot tell you — harness behavior, tool latency, retrieval depth, failure recovery.',
    meta: 'Skill package · agent benchmarks',
    href: '/skills/benchmarking-ai-agents-beyond-models',
  },
  {
    slug: 'detecting-harness-lockin',
    title: 'detecting-harness-lockin',
    summary: 'Flags the subtle assumptions that quietly couple your stack to a single harness. Hard to spot, expensive to unwind.',
    meta: 'Skill package · risk signal',
    href: '/skills/detecting-harness-lockin',
  },
  {
    slug: 'evaluating-ai-harness-dimensions',
    title: 'evaluating-ai-harness-dimensions',
    summary: 'A structured way to compare AI harnesses across the dimensions that actually matter in production — not the ones on the marketing page.',
    meta: 'Skill package · harness eval',
    href: '/skills/evaluating-ai-harness-dimensions',
  },
  {
    slug: 'routing-work-across-ai-harnesses',
    title: 'routing-work-across-ai-harnesses',
    summary: 'Routes tasks across multiple harnesses based on capability, cost, and latency. Pragmatic patterns for multi-harness deployments.',
    meta: 'Skill package · multi-harness',
    href: '/skills/routing-work-across-ai-harnesses',
  },
  {
    slug: 'diagnosing-rag-failure-modes',
    title: 'diagnosing-rag-failure-modes',
    summary: 'Fixes the most common RAG pipeline failures — chunking errors, retrieval mismatch, context collapse, and the subtle ones nobody documents.',
    meta: 'Skill package · RAG forensics',
    href: '/skills/diagnosing-rag-failure-modes',
  },
  {
    slug: 'synthesizing-institutional-knowledge',
    title: 'synthesizing-institutional-knowledge',
    summary: 'Turns scattered docs, decisions, and tribal lore into a coherent institutional memory your agent can actually reason over.',
    meta: 'Skill package · knowledge synthesis',
    href: '/skills/synthesizing-institutional-knowledge',
  },
  {
    slug: 'temporal-reasoning-sleuth',
    title: 'temporal-reasoning-sleuth',
    summary: 'Traces decision chains across long event timelines. Resolves contradictions, normalizes time anchors, and reconstructs causality from messy logs.',
    meta: 'Skill package · temporal logic',
    href: '/skills/temporal-reasoning-sleuth',
  },
  {
    slug: 'designing-hybrid-context-layers',
    title: 'designing-hybrid-context-layers',
    summary: 'Combines vector search, graph traversal, and structured data into a single retrieval surface. Production patterns, not hand-wavy diagrams.',
    meta: 'Skill package · hybrid retrieval',
    href: '/skills/designing-hybrid-context-layers',
  },
  {
    slug: 'agentic-rag-routing',
    title: 'agentic-rag-routing',
    summary: 'Routes retrieval-augmented queries through an agent loop that decides what to fetch, when to stop, and how to reconcile sources.',
    meta: 'Skill package · agentic retrieval',
    href: '/skills/agentic-rag-routing',
  },
];

export const APPROACH = [
  {
    k: 'Point.',
    v: 'Hand Loreto any URL or file. One endpoint. No type juggling. No pre-processing dance.',
  },
  {
    k: 'Detect.',
    v: 'Source type is inferred automatically — youtube, article, pdf, image — or you can pin it explicitly.',
  },
  {
    k: 'Extract.',
    v: 'Loreto does the work in a single response: parse, rank, package. You get ready-to-load skills, not transcripts.',
  },
  {
    k: 'Compound.',
    v: 'Each skill lives in .claude/skills/ and persists across every future session. Your agent gets permanently smarter.',
  },
];

export const HOW = [
  { n: '01', title: 'Install', body: 'pip install loreto-mcp. One line. No SDK choreography. No background daemon.', code: 'pip install loreto-mcp' },
  { n: '02', title: 'Wire it up', body: 'Add the entry to ~/.claude/mcp.json. Claude Code sees it on next launch — that is the whole config step.', code: '~/.claude/mcp.json' },
  { n: '03', title: 'Point Claude', body: '“Use Loreto to extract skills from this and save to .claude/skills/.” That is the prompt. Pass the URL with it.', code: 'extract → .claude/skills/' },
  { n: '04', title: 'It just knows', body: 'Claude Code picks the skills up automatically. No re-prompting. No reloading. The next session already has them.', code: 'auto-load ✓' },
];

export const STATS = [
  { value: '2', unit: 'calls / month', note: 'Free tier, every account.' },
  { value: '3', unit: 'skills per call', note: 'Up to three ranked skill packages per source.' },
  { value: '6', unit: 'skills, free', note: 'What the free tier compounds to, every month.' },
  { value: '72', unit: 'skills / year', note: 'Six every month, all yours forever — what a free account compounds to in a year.' },
];

export const FOOTER_LINKS = {
  Product: [
    { label: 'Home', href: '/' },
    { label: 'Developer Docs', href: '/docs' },
    { label: 'Marketplace', href: '/marketplace' },
    { label: 'Git Timeline', href: '/git-timeline' },
    { label: 'Blog', href: '/blog' },
    { label: 'Field papers', href: '/papers' },
    { label: 'Consulting', href: '/consulting' },
  ],
  Build: [
    { label: 'GitHub', href: 'https://github.com/kopias/loreto-mcp' },
    { label: 'Get keys', href: '/dashboard' },
    { label: 'Loreto API', href: '/api' },
  ],
  // Static SEO pages — `native: true` forces a full-page <a> navigation (these
  // are server-rendered static pages, not SPA routes, so react-router <Link>
  // would miss them).
  Resources: [
    { label: 'Free tools', href: '/tools', native: true },
    { label: 'Free skills', href: '/free-skills', native: true },
    { label: 'Compare', href: '/compare', native: true },
    { label: 'By use case', href: '/for', native: true },
    { label: 'Best skills', href: '/best', native: true },
    { label: 'Glossary', href: '/glossary', native: true },
  ],
  Loreto: [
    { label: 'Privacy Policy', href: '/legal/privacy' },
    { label: 'Terms of Service', href: '/legal/terms' },
    { label: 'DMCA Policy', href: '/legal/dmca' },
    { label: 'Responsible Disclosure', href: '/legal/responsible-disclosure' },
    { label: 'Stripe Payment Terms', href: '/legal/stripe-terms' },
  ],
};

export const LINKS = {
  keys: '/dashboard',
  github: 'https://github.com/kopias/loreto-mcp',
};
